import React from 'react'
import { motion } from 'framer-motion'
import { useApp } from '../contexts/AppContext'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiHeart, FiCode, FiMail, FiInstagram, FiFacebook } = FiIcons

// Threads 官方 SVG Logo
const ThreadsIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 192 192"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M141.537 88.988a66.667 66.667 0 0 0-2.518-1.143c-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.695 14.724-10.548 21.348-10.548h.229c8.249.053 14.474 2.452 18.503 7.129 2.932 3.405 4.893 8.11 5.864 14.05-7.314-1.244-15.224-1.626-23.68-1.14-23.82 1.371-39.134 15.264-38.105 34.568.522 9.792 5.4 18.216 13.735 23.719 7.047 4.652 16.124 6.927 25.557 6.412 12.458-.683 22.231-5.436 29.049-14.127 5.178-6.6 8.453-15.153 9.899-25.93 5.937 3.583 10.337 8.298 12.767 13.966 4.132 9.635 4.373 25.468-8.546 38.376-11.319 11.308-24.925 16.2-45.488 16.351-22.809-.169-40.06-7.484-51.275-21.742C35.236 139.966 29.808 120.682 29.605 96c.203-24.682 5.63-43.966 16.133-57.317C56.954 24.425 74.206 17.11 97.015 16.94c22.975.17 40.526 7.52 52.171 21.847 5.71 7.026 10.015 15.86 12.853 26.162l16.147-4.308c-3.44-12.68-8.853-23.606-16.219-32.668C147.036 9.607 125.202.195 97.105 0h-.113C68.884.195 47.255 9.65 32.604 28.05 19.515 44.44 12.765 67.434 12.508 96v.027c.257 28.593 7.007 51.587 20.096 68.002C47.255 182.358 68.884 191.812 96.992 192h.113c24.96-.173 42.554-6.708 57.048-21.189 18.963-18.945 18.392-42.692 12.142-57.27-4.484-10.454-13.033-18.944-24.758-24.553Z"/>
  </svg>
)

const SocialLink = ({ href, icon, label, hoverColor, customIcon }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className={`text-gray-400 dark:text-gray-500 transition-colors duration-200 ${hoverColor}`}
  >
    {customIcon ? customIcon : <SafeIcon icon={icon} className="w-4 h-4" aria-hidden="true" />}
  </a>
)

const Footer = () => {
  const { currentLang, translations } = useApp()
  const currentYear = new Date().getFullYear()

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.8 }}
      className="py-6 sm:py-8 text-center text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-800"
      role="contentinfo"
      aria-label={currentLang === 'zh' ? '網站頁尾' : 'Site footer'}
      itemScope
      itemType="https://schema.org/WPFooter"
    >
      <div className="max-w-4xl mx-auto px-4 space-y-3 sm:space-y-4">

        {/* Copyright */}
        <p className="text-xs sm:text-sm" itemProp="copyrightNotice">
          <span itemProp="copyrightYear">{currentYear}</span> {translations.footerCopyright[currentLang]}
        </p>

        {/* Developer Info */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 text-xs">
          <div className="flex items-center gap-1 sm:gap-2">
            <SafeIcon icon={FiCode} className="w-3 h-3 text-blue-500" aria-hidden="true" />
            <span className="text-gray-400 dark:text-gray-500">
              {translations.footerMadeBy[currentLang]}
            </span>
            <SafeIcon icon={FiHeart} className="w-3 h-3 text-red-500 animate-pulse" aria-hidden="true" />
          </div>
          <div itemScope itemType="https://schema.org/Organization">
            <a
              href={translations.footerCompanyUrl[currentLang]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 dark:text-purple-400 font-medium hover:text-purple-700 dark:hover:text-purple-300 transition-colors duration-200"
              itemProp="url"
              aria-label={`Visit ${translations.footerCompany[currentLang]} website`}
            >
              <span itemProp="name">{translations.footerCompany[currentLang]}</span>
            </a>
          </div>
        </div>

        {/* Social Media + Contact Links */}
        <div className="flex items-center justify-center gap-4 pt-1">
          <SocialLink
            href="https://www.threads.com/@homy.hk"
            label="Follow us on Threads"
            hoverColor="hover:text-gray-800 dark:hover:text-white"
            customIcon={<ThreadsIcon className="w-4 h-4" />}
          />
          <SocialLink
            href="https://www.instagram.com/homy.hk"
            icon={FiInstagram}
            label="Follow us on Instagram"
            hoverColor="hover:text-pink-500 dark:hover:text-pink-400"
          />
          <SocialLink
            href="https://www.facebook.com/profile.php?id=61578745554661"
            icon={FiFacebook}
            label="Follow us on Facebook"
            hoverColor="hover:text-blue-600 dark:hover:text-blue-400"
          />
          <a
            href="mailto:info@homy.hk"
            className="text-gray-400 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-200"
            aria-label="Contact us via email"
          >
            <SafeIcon icon={FiMail} className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>

        {/* SEO Keywords - Hidden but crawlable */}
        <div className="sr-only" aria-hidden="true">
          Hong Kong bank codes, 香港銀行代碼, HSBC code 004, Hang Seng code 024, Standard Chartered code 003,
          Bank of China HK code 012, virtual banks Hong Kong, ZA Bank 387, Livi Bank 388, MOX Bank 389,
          WeLab Bank 390, clearing codes, HKMA banks, 香港金管局銀行名單, bank directory 2026
        </div>
      </div>
    </motion.footer>
  )
}

export default Footer